/**
 * Mock API Configuration
 * Set ENABLE_MOCK to true to use mock data instead of real API calls
 */

export const MOCK_CONFIG = {
  ENABLE_MOCK: process.env.REACT_APP_USE_MOCK_DATA === 'true' || false,
  MOCK_DATA_PATH: '/mockData',
  // Log when mock data is being used
  LOG_MOCK_USAGE: true,
  // Delay to simulate network latency (ms)
  MOCK_DELAY: 500
};

/**
 * Mock request matcher configuration
 * Maps API endpoints to mock data file names
 */
export const MOCK_ENDPOINTS = {
  // Rig Overview endpoints
  '/RigOverview': 'rigOverview.json',
  '/RigInfo': 'rigInfo.json',
  '/RigOverviewKeyFigures': 'rigOverviewKeyFigures.json',
  
  // Equipment endpoints
  '/Equipment/GetAllComponentConsumptionsAggregated': 'getAllComponentConsumptionsAggregated.json',
  '/Equipment/GetAllComponentConsumptions': 'getAllComponentConsumptions.json',
  
  // Power Management
  '/PowerMgmt': 'powerMgmt.json',
  
  // Analytics
  '/Analytics': 'analytics.json',
  
  // Drilling Activities
  '/DrillingActivities': 'drillingActivities.json',
  
  // Focus Area / Warning endpoints
  '/HPUFocusArea': 'hpuFocusArea.json',
  '/EngineFocusArea': 'engineFocusArea.json',
  '/MudTreatmentFocusArea': 'mudTreatmentFocusArea.json'
};

/**
 * Sanitize a string to be safe for use in filenames
 * Replaces characters not allowed in Windows/Unix filenames
 */
const sanitizeForFilename = (str) => {
  return String(str)
    // Replace characters not allowed in filenames: < > : " / \ | ? *
    .replace(/[<>:"/\\|?*]/g, '_')
    // Replace spaces with underscores
    .replace(/\s+/g, '_')
    // Replace plus signs (common in URL encoding)
    .replace(/\+/g, '_')
    // Replace multiple consecutive underscores with single underscore
    .replace(/_+/g, '_')
    // Remove leading/trailing underscores
    .replace(/^_+|_+$/g, '');
};

/**
 * Extract date portion (YYYY-MM-DD) from ISO date string
 */
const extractDateOnly = (dateStr) => {
  // Match YYYY-MM-DD pattern at the beginning
  const match = String(dateStr).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : dateStr;
};

/**
 * Generate mock file path for a given endpoint and query params
 * This allows for parameterized mock files
 * e.g., /RigOverview?rigId=123&startDate=2024-01-01T10:00:00Z -> rigOverview_rigId_123_startDate_2024-01-01.json
 */
export const generateMockFileName = (endpoint, queryParams = {}) => {
  const baseName = MOCK_ENDPOINTS[endpoint];
  if (!baseName) return null;
  
  // For simple cases, just return the base name
  if (Object.keys(queryParams).length === 0) {
    return baseName;
  }
  
  // Date parameters to simplify to day-only
  const dateParams = ['startDate', 'endDate', 'focusAreaStartDate', 'focusAreaEndDate'];
  
  // For parameterized cases, append all query params to filename
  const nameWithoutExt = baseName.replace('.json', '');
  const paramPairs = Object.entries(queryParams)
    .filter(([key, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => {
      // For date parameters, extract only the date part (YYYY-MM-DD)
      const processedValue = dateParams.includes(key) ? extractDateOnly(value) : value;
      return `${sanitizeForFilename(key)}_${sanitizeForFilename(processedValue)}`;
    })
    .join('_');
  
  if (paramPairs) {
    return `${nameWithoutExt}_${paramPairs}.json`;
  }
  
  return baseName;
};
